import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";
import { useFieldArray } from "react-hook-form";
import FormSelectField from "../../Common/FormSelectField";
import FormTextField from "../../Common/FormTextField";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "./ConfigFormContext";

const ConfigSplit: React.FC = () => {
  const { control, watch, setValue } = useFormConfigContext();
  const { isReadOnly } = useConfigModeContext();

  const {
    fields: splitClasses,
    append: addSplitClass,
    remove: removeSplitClass,
  } = useFieldArray({
    control,
    name: "config.split.segmentation.classes",
  });

  const split_classes = watch("config.split.segmentation.classes");
  const split_default = watch("config.split.segmentation.default");
  const allClasses = [
    ...split_classes,
    ...(split_default ? [split_default] : []),
  ];
  const someClasses =
    allClasses.filter((classe) => classe.name !== "").length > 0;
  return (
    <>
      <div className="flex justify-between items-center">
        <Typography className="text-xl font-semibold">Découpage</Typography>
        <div className="flex gap-2">
          {!isReadOnly && (
            <Button
              color="error"
              onClick={() => setValue("config.split", undefined)}
            >
              <Delete />
            </Button>
          )}
        </div>
      </div>

      <Stack rowGap={2} className="px-4 py-4 overflow-y-scroll">
        <Typography className="text-lg font-semibold">Segmentation</Typography>
        <Typography className="text-sm italic">
          L’étape du découpage permet de découper automatiquement un seul et
          même document selon un ensemble de types précisés. Si cette étape est
          définie, elle s’effectuera sur chacun des documents présent dans le
          dossier. Définir ici l’ensemble des types de documents que l’on peut
          trouver dans le dossier.
        </Typography>
        <div className="flex justify-between items-center">
          <Typography className="text-md font-semibold">Classes</Typography>
          {!isReadOnly && (
            <Button
              onClick={() =>
                addSplitClass({
                  name: "",
                })
              }
            >
              <Add fontSize="small" />
            </Button>
          )}
        </div>

        {splitClasses?.map((classe, index) => (
          <div
            key={`config.split.segmentation.classes.${classe.id}`}
            className="flex flex-1 gap-2"
          >
            <FormTextField
              size="small"
              control={control}
              name={`config.split.segmentation.classes.${index}.name`}
              label="Nom de la classe"
              className="w-1/5"
            />
            <FormTextField
              size="small"
              control={control}
              name={`config.split.segmentation.classes.${index}.description`}
              label="Description de la classe"
              className="w-4/5"
            />
            {!isReadOnly && splitClasses.length > 2 && (
              <Button onClick={() => removeSplitClass(index)}>
                <Delete color="error" />
              </Button>
            )}
          </div>
        ))}
        <div className="flex justify-between items-center">
          <Typography className="text-md font-semibold">
            Classe par défaut
          </Typography>
          {!isReadOnly &&
            (split_default ? (
              <Button
                onClick={() =>
                  setValue("config.split.segmentation.default", undefined)
                }
              >
                <Delete color="error" />
              </Button>
            ) : (
              <Button
                onClick={() =>
                  setValue("config.split.segmentation.default", {
                    name: "",
                  })
                }
                disabled={!!split_default}
              >
                <Add fontSize="small" />
              </Button>
            ))}
        </div>

        {split_default ? (
          <div className="flex flex-1 flex-col gap-2">
            <FormTextField
              size="small"
              control={control}
              name={`config.split.segmentation.default.name`}
              label="Nom de la classe par défaut"
            />
            <FormTextField
              size="small"
              control={control}
              name={`config.split.segmentation.default.description`}
              label="Description de la classe par défaut"
            />
          </div>
        ) : (
          <Typography className="text-sm italic">
            Aucune classe par défaut. En cas d'erreur ou si le modèle n'est pas
            capable de classifier le document parmis les classes données, la
            classe par défaut sera utilisée
          </Typography>
        )}
        <Typography className="text-lg font-semibold">Regroupement</Typography>
        <Typography className="text-sm italic">
          Vous pouvez préciser ici deux types de documents spécifiques. Les
          pages de type IGNORER seront regroupées avec les documents précédents.
          Des pages successives de type FUSIONNER seront regroupées les unes
          avec les autres.
        </Typography>
        <FormSelectField
          size="small"
          control={control}
          label="Fusionner"
          name="config.split.group.weak_types"
          disabled={!someClasses}
          options={allClasses.map((classe) => classe.name)}
        />
        <FormSelectField
          size="small"
          control={control}
          label="Ignorer"
          name="config.split.group.ghost_types"
          disabled={!someClasses}
          options={allClasses.map((classe) => classe.name)}
        />
      </Stack>
    </>
  );
};
export default ConfigSplit;
